import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { getLocation } from '../../../account-group/account-group';
import { LocationsService } from '../../../locations';
import { BrandFilterContainerService, FilterDisplaySettings } from '../brand-filter-container.service';

export interface LocationData {
  filterSettings: FilterDisplaySettings;
}

export interface LocationDisplay {
  accountGroupId: string;
  name: string;
  address: string;
}

@Component({
  selector: 'bc-brand-geography-dialog',
  templateUrl: './brand-location-dialog.component.html',
  styleUrls: ['./brand-location-dialog.component.scss'],
  standalone: false,
})
export class BrandLocationDialogComponent implements OnInit {
  accountGroupFilterControl = new UntypedFormControl();

  options$: Observable<LocationDisplay[]>;
  filteredOptions$: Observable<LocationDisplay[]>;

  selectedOptionsDetails$: Observable<LocationDisplay[]>;
  selectedOptions$$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  selectedOptions$: Observable<string[]> = this.selectedOptions$$.asObservable();

  noResultsFound = false;

  constructor(
    public dialogRef: MatDialogRef<BrandLocationDialogComponent>,
    private brandFiltersService: BrandFilterContainerService,
    @Inject(MAT_DIALOG_DATA) public data: LocationData,
    private locationsService: LocationsService,
  ) {}

  ngOnInit(): void {
    this.selectedOptions$$.next(this.brandFiltersService.getAccountGroupIds());

    this.options$ = this.locationsService.currentAccountGroups$.pipe(
      map((locationMap) => {
        if (!locationMap) {
          return [];
        }
        this.accountGroupFilterControl.setValue('');
        return Object.keys(locationMap)
          .map((accountGroupId) => {
            const accountGroup = locationMap[accountGroupId];
            return {
              accountGroupId,
              name: accountGroup.companyName,
              address: getLocation(accountGroup),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
      }),
    );

    this.filteredOptions$ = combineLatest([
      this.options$,
      this.accountGroupFilterControl.valueChanges,
      this.selectedOptions$,
    ]).pipe(
      debounceTime(500),
      map(([options, value, selectedOptions]) => this._filter(options, value, selectedOptions)),
      tap((options) => {
        this.noResultsFound = options.length === 0;
      }),
    );

    this.selectedOptionsDetails$ = combineLatest([this.options$, this.selectedOptions$]).pipe(
      map(([options, selected]) => {
        return options.filter((o) => selected.indexOf(o.accountGroupId) > -1);
      }),
    );
  }

  private _filter(options: LocationDisplay[], value: string, selectedOptions: string[]): LocationDisplay[] {
    if (value.trim() === '') {
      return options.filter((option) => selectedOptions.indexOf(option.accountGroupId) === -1);
    }
    const filterValue = value.toUpperCase();
    return options
      .filter(
        (option) =>
          option.name.toUpperCase().includes(filterValue) || option.address.toUpperCase().includes(filterValue),
      )
      .filter((option) => selectedOptions.indexOf(option.accountGroupId) === -1);
  }

  selectFilterLocation(option: HTMLElement, accountGroupId: string): void {
    option.remove();
    const currentSelected = this.selectedOptions$$.getValue();
    if (currentSelected.indexOf(accountGroupId) === -1) {
      currentSelected.push(accountGroupId);
    }
    this.selectedOptions$$.next(currentSelected);
  }

  removeLocation(accountGroupId: string): void {
    this.selectedOptions$$.next(this.selectedOptions$$.getValue().filter((o) => o !== accountGroupId));
  }

  close(newData: boolean): void {
    if (!newData) {
      this.dialogRef.close();
      return;
    }
    this.dialogRef.close({
      selectedAccountGroupIds: this.selectedOptions$$.getValue(),
    });
  }
}
